// FILE: src/pages/BillingSuccess.jsx
import React, { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { createPortalLink } from "../lib/billingApi";
import Menu from "../Menu";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id"); // appended by Stripe when using {CHECKOUT_SESSION_ID}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showMenu, setShowMenu] = useState(false);

  // Use the current Firebase user (same behaviour as before)
  const uid = useMemo(() => getAuth().currentUser?.uid || null, []);

  async function openPortal() {
    try {
      setBusy(true);
      setErr("");
      if (!uid) throw new Error("You need to be signed in to manage billing.");
      const { url } = await createPortalLink({
        uid,
        return_url: window.location.origin + "/pricing",
      });
      window.location.href = url;
    } catch (e) {
      setErr(e?.message || "Failed to open billing portal.");
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "#223b4c", color: "#f8faff" }}>
      {/* Top Bar with Menu Button */}
      <div
        style={{
          width: "100vw",
          padding: "14px 16px",
          background: "rgba(20,15,44,0.55)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
      >
        <button
          aria-label="Open menu"
          title="Menu"
          onClick={() => setShowMenu(true)}
          style={{
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#ffe066",
            fontWeight: 900,
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
          }}
        >
          ☰ Menu
        </button>

        <div style={{ marginLeft: 8, fontWeight: 900, color: "#f8faff" }}>
          Payment successful
        </div>
      </div>

      {/* Sidebar instance */}
      <Menu open={showMenu} onClose={() => setShowMenu(false)} />

      {/* Page content */}
      <div style={{ maxWidth: 720, margin: "40px auto", padding: 16 }}>
        <div
          style={{
            background: "rgba(32,30,48,0.58)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: 20,
            padding: 24,
            boxShadow: "0 8px 36px 8px #1116",
          }}
        >
          <h1 style={{ marginTop: 0 }}>🎉 Payment successful</h1>
          <p style={{ marginTop: 8 }}>
            Thanks! Your subscription is active. You can return to{" "}
            <Link to="/pricing" style={{ color: "#9effa8", fontWeight: 800, textDecoration: "underline" }}>
              Pricing
            </Link>{" "}
            or start hosting quizzes right away.
          </p>

          {sessionId && (
            <p style={{ opacity: 0.75, fontSize: 14, marginTop: 8 }}>
              Checkout session: <code>{sessionId}</code>
            </p>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
            <Link to="/host">
              <button
                style={{
                  padding: "10px 16px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "linear-gradient(135deg, rgba(255,204,102,0.90), rgba(255,150,64,0.95))",
                  color: "#1d2030",
                  fontWeight: 900,
                  cursor: "pointer",
                }}
              >
                Go to Host
              </button>
            </Link>

            <Link to="/pricing">
              <button
                style={{
                  padding: "10px 16px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(32,30,48,0.58)",
                  color: "#f8faff",
                  cursor: "pointer",
                }}
              >
                Back to Pricing
              </button>
            </Link>

            {uid && (
              <button
                onClick={openPortal}
                disabled={busy}
                style={{
                  padding: "10px 16px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.18)",
                  background: "rgba(32,30,48,0.58)",
                  color: "#f8faff",
                  cursor: "pointer",
                  opacity: busy ? 0.8 : 1,
                }}
              >
                {busy ? "Opening…" : "Manage Subscription"}
              </button>
            )}
          </div>

          {err && (
            <p style={{ color: "tomato", marginTop: 12 }}>
              {err}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
